import { client } from "../lib/client.js";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { ApiError, type JobRow } from "@gwrctl/api-client";
import { getJob } from "@gwrctl/shared";
import { humanizeError } from "../lib/errors.ts";
import { useRunJob } from "../lib/jobs.ts";
import { useToast } from "../lib/toast.tsx";
import { Button, ConfirmButton } from "./Ui.tsx";

/**
 * Tek bir işi bulunduğu yerde çalıştıran düğme. Onay, paylaşılan job tanımındaki
 * seviyeye göre: "none" → düz düğme, "click" → iki tıklama, "totp" → satır içi kod.
 * Sonuç bildirim olarak gelir; hata sessizce yutulmaz.
 *
 * Pano, Bot ve Sunucu sayfaları kullanıyor.
 */
export function JobAction({
  type,
  params,
  label,
  children,
  variant = "secondary",
  size = "sm",
  icon,
  wait = true,
  quiet = false,
  disabled,
  className,
  onDone,
}: {
  type: string;
  params: Record<string, unknown>;
  /** Bildirimlerde işin adı. */
  label: string;
  children: ReactNode;
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md";
  icon?: LucideIcon;
  /** false: iş başlar başlamaz döner (canlı log gibi süreç işleri). */
  wait?: boolean;
  /** true: başarı bildirimi gösterme (çağıran zaten iş sayfasına gidiyor). */
  quiet?: boolean;
  disabled?: boolean;
  className?: string;
  onDone?: (job: JobRow) => void | Promise<void>;
}): ReactNode {
  const { run, busy } = useRunJob();
  const toast = useToast();
  const declared = getJob(type)?.confirm ?? "click";
  // TOTP onayı taze ise (son 8 saat) kod yerine iki tıklamalı onay yeter.
  const me = useQuery({ queryKey: ["me"], queryFn: () => client.me(), staleTime: 30_000 });
  const level = declared === "totp" && me.data?.totpFresh ? "click" : declared;
  const [askCode, setAskCode] = useState(false);
  const [code, setCode] = useState("");

  async function go(totp?: string): Promise<void> {
    try {
      const job = await run({ type, params, ...(totp ? { totp } : {}), wait });
      setAskCode(false);
      setCode("");
      if (!quiet) {
        toast({
          tone: "ok",
          title: wait ? `${label}: tamamlandı` : `${label}: başlatıldı`,
          link: { to: `/jobs/${job.id}`, label: "İş kaydı" },
        });
      }
      await onDone?.(job);
    } catch (err) {
      // Sunucu kod isterse (politika değişmiş olabilir) satır içi kod alanına düş.
      if (err instanceof ApiError && err.needsTotp) {
        setAskCode(true);
        setCode("");
      }
      toast({ tone: "danger", title: `${label} başarısız`, body: humanizeError(err) });
    }
  }

  if (askCode) {
    return (
      <span className={`inline-flex items-center gap-1.5 ${className ?? ""}`}>
        <input
          className="input mono !w-[96px] text-center tracking-[0.2em]"
          aria-label={`${label} için 6 haneli doğrulama kodu`}
          placeholder="kod"
          value={code}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          autoFocus
          disabled={busy}
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, "").slice(0, 6);
            setCode(v);
            if (v.length === 6 && !busy) void go(v);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") setAskCode(false);
          }}
        />
        <Button
          size={size}
          variant={variant}
          loading={busy}
          disabled={code.length !== 6}
          onClick={() => void go(code)}
        >
          Onayla
        </Button>
        <Button size={size} variant="ghost" disabled={busy} onClick={() => setAskCode(false)}>
          Vazgeç
        </Button>
      </span>
    );
  }

  if (level === "totp") {
    return (
      <Button
        size={size}
        variant={variant}
        icon={icon}
        className={className}
        disabled={disabled}
        loading={busy}
        onClick={() => setAskCode(true)}
      >
        {children}
      </Button>
    );
  }

  if (level === "click") {
    return (
      <ConfirmButton
        size={size}
        variant={variant}
        icon={icon}
        className={className}
        disabled={disabled}
        loading={busy}
        onConfirm={() => go()}
      >
        {children}
      </ConfirmButton>
    );
  }

  return (
    <Button
      size={size}
      variant={variant}
      icon={icon}
      className={className}
      disabled={disabled}
      loading={busy}
      onClick={() => void go()}
    >
      {children}
    </Button>
  );
}
